import { z } from "zod";
import { ImplementationType, offersCustomCaseStrategy } from "../../models/OrganizationConfiguration/ImplementationType";
import { CaseMigrationStrategy } from "../../models/OrganizationConfiguration/CaseMigrationStrategy";
import { MigrationStrategy } from "../../models/OrganizationConfiguration/MigrationStrategy";

/**
 * Zod schema for the OrganizationConfiguration form.
 * Mirrors backend PatchOrganizationConfigurationCommandValidator.
 */
export const organizationConfigurationSchema = z
  .object({
    implementationType: z.nativeEnum(ImplementationType).nullable().optional(),
    casesMigrationStrategy: z
      .array(z.nativeEnum(CaseMigrationStrategy))
      .nullable()
      .optional(),
    providersMigrationStrategy: z
      .nativeEnum(MigrationStrategy)
      .nullable()
      .optional(),
    templateFilesMigrationStrategy: z
      .nativeEnum(MigrationStrategy)
      .nullable()
      .optional(),
    firmDocumentsMigrationStrategy: z
      .nativeEnum(MigrationStrategy)
      .nullable()
      .optional(),
    migrateOpenedCases: z.boolean(),
    migrateClosedCases: z.boolean(),
    casesOpenedAfter: z.string().nullable().optional(),
    casesClosedAfter: z.string().nullable().optional(),
    casesOpenedBefore: z.string().nullable().optional(),
    casesClosedBefore: z.string().nullable().optional(),
    caseSpecifiedList: z.array(z.string()).nullable().optional(),
    casePriorityList: z.array(z.string()).nullable().optional(),
    caseIgnoredList: z.array(z.string()).nullable().optional(),
    caseFilesPriorityList: z.array(z.string()).nullable().optional(),
  })
  .passthrough()
  // Use `.check()` instead of `.superRefine()` — in zod v4, `.superRefine()`
  // is skipped when field-level parses fail, which would hide cross-field
  // errors. `.check()` runs regardless.
  .check((ctx) => {
    const data = ctx.value;
    const addIssue = (path: (string | number)[], message: string) =>
      ctx.issues.push({ code: "custom", path, message, input: data });

    // Date bounds only apply under Custom strategy — same gate the UI uses to
    // show the date rows. Skipping when Custom is off prevents stale values
    // (left over from a prior Custom session) from raising errors on hidden
    // fields.
    const usesCustom = Array.isArray(data.casesMigrationStrategy)
      && data.casesMigrationStrategy.includes(CaseMigrationStrategy.Custom)
      && offersCustomCaseStrategy(data.implementationType);
    if (!usesCustom) return;

    // Compare only the calendar-date prefix (yyyy-mm-dd). Stored values arrive as full
    // timestamps (yyyy-mm-ddT00:00:00) while freshly-picked ones are date-only, so a raw
    // string compare would mis-order the two forms on the same day.
    const dateKey = (value: string) => value.slice(0, 10);

    if (
      data.migrateOpenedCases &&
      data.casesOpenedAfter &&
      data.casesOpenedBefore &&
      dateKey(data.casesOpenedBefore) < dateKey(data.casesOpenedAfter)
    ) {
      addIssue(["casesOpenedBefore"], "Opened before must be on or after opened after.");
    }
    if (
      data.migrateClosedCases &&
      data.casesClosedAfter &&
      data.casesClosedBefore &&
      dateKey(data.casesClosedBefore) < dateKey(data.casesClosedAfter)
    ) {
      addIssue(["casesClosedBefore"], "Closed before must be on or after closed after.");
    }
  });
